// =====================================================================
// src/lib/manutencao.js — o que a lista de chamados DECIDE, sem DOM
//
// Quem vê o quê NÃO está aqui: está no RLS e em `chamados_do_cliente`
// (0029/0030). Quando esta biblioteca recebe uma linha, o banco já decidiu
// que aquela pessoa pode vê-la. O que sobra para cá é o que a TELA decide:
// separar o que é meu do que é ciência, ordenar, e escrever em português o
// que a coluna guarda em uma palavra.
//
// Tudo puro, testado em node (scripts/teste_tela.mjs).
// =====================================================================

export const SITUACOES = { aberto: 'Aberto', resolvido: 'Resolvido', cancelado: 'Cancelado' };

// ── ONDE O CHAMADO ESTÁ, E O QUE SE FAZ NELE (0033 + 0059) ───────────
//   triagem     → a camareira reportou; a SUPERVISORA valida ou descarta
//   aberto      → com a MANUTENÇÃO: resolve, ou diz que não consegue
//   sem_solucao → volta à SUPERVISORA: avisa a imobiliária, devolve à
//                 manutenção, ou resolve
//   escalado    → com a IMOBILIÁRIA do flat (e a supervisão), que fecha (0099)
// Quem pode agir vem do banco em `para_mim`; aqui só se escolhe QUAIS
// botões — sem `para_mim`, nenhum.
export const ETAPAS = Object.freeze({
  triagem:     { rotulo: 'Aguardando a supervisora validar', classe: 'etapa-triagem' },
  aberto:      { rotulo: 'Com a manutenção',                 classe: 'etapa-manutencao' },
  sem_solucao: { rotulo: 'Manutenção não resolveu — com a supervisora', classe: 'etapa-supervisao' },
  escalado:    { rotulo: 'Imobiliária avisada',              classe: 'etapa-imobiliaria' },
  resolvido:   { rotulo: 'Resolvido',                        classe: 'etapa-resolvido' },
  cancelado:   { rotulo: 'Descartado',                       classe: 'etapa-cancelado' },
});
export function etapaDe(situacao) {
  return ETAPAS[situacao] || { rotulo: situacao || '—', classe: '' };
}
export function acoesDoChamado(c) {
  if (!c || !c.para_mim) return [];
  switch (c.situacao) {
    case 'triagem':     return ['validar', 'descartar'];
    case 'aberto':      return ['resolver', 'sem_solucao'];
    case 'sem_solucao': return ['escalar', 'devolver', 'resolver'];
    case 'escalado':    return ['resolver'];
    default:            return [];
  }
}
// O que cada ação pede de texto. `obrigatorio` espelha o banco: sem
// motivo ele recusa (descartar, sem solução, devolver).
export const ACOES_DO_CHAMADO = Object.freeze({
  validar:     { rotulo: 'Enviar à manutenção',        rpc: 'validar_chamado',     campo: null },
  descartar:   { rotulo: 'Descartar',                  rpc: 'descartar_chamado',   campo: 'p_motivo', obrigatorio: true,  dica: 'Por que não é conserto? A camareira vai ler.' },
  resolver:    { rotulo: 'Resolver',                   rpc: 'resolver_chamado',    campo: 'p_obs',    obrigatorio: false, dica: 'O que foi feito? Ex.: vaso desentupido.' },
  sem_solucao: { rotulo: 'Não consigo resolver',       rpc: 'sem_solucao_chamado', campo: 'p_motivo', obrigatorio: true,  dica: 'Por que não foi possível? Ex.: o ar-condicionado é da imobiliária.' },
  escalar:     { rotulo: 'Avisar a imobiliária',       rpc: 'escalar_chamado',     campo: 'p_motivo', obrigatorio: false, dica: 'O que a imobiliária precisa fazer? (opcional)' },
  devolver:    { rotulo: 'Devolver à manutenção',      rpc: 'devolver_chamado',    campo: 'p_motivo', obrigatorio: true,  dica: 'O que a manutenção deve fazer?' },
});

// De quem é o conserto. A decisão do Hugo em 17/09: ar-condicionado split,
// TV e secador são do flat — e quem responde pelo flat é a imobiliária (0099,
// 02/11/2026); o resto da manutenção é do hotel.
export const RESPONSAVEIS = {
  hotel:        { rotulo: 'Hotel', curto: 'HOTEL', classe: 'hotel' },
  imobiliaria:  { rotulo: 'Imobiliária do flat', curto: 'IMOBILIÁRIA', classe: 'imobiliaria' },
};
export function responsavelDe(chave) {
  return RESPONSAVEIS[chave] || { rotulo: chave || '—', curto: (chave || '—').toUpperCase(), classe: 'hotel' };
}

// De onde o chamado veio. Importa para quem conserta: um "Solicito" da
// ficha tem item e nome; uma intercorrência tem descrição livre e foto.
export const ORIGENS = {
  ficha:          { rotulo: 'Ficha da camareira', icone: 'ic-clipboard' },
  intercorrencia: { rotulo: 'Intercorrência',     icone: 'ic-warning' },
};
export function origemDe(chave) {
  return ORIGENS[chave] || { rotulo: chave || '—', icone: 'ic-wrench' };
}

// ── as duas pilhas ───────────────────────────────────────────────────
// `para_mim` vem do banco e quer dizer "EU posso fechar este". A tela
// separa nisso, e não por responsável: para a manutenção do hotel, "do
// hotel" e "para mim" são a mesma coisa; para a supervisão, não são — ela
// acompanha a fila inteira e só age na etapa dela. Separar pelo que a
// pessoa PODE FAZER é o que faz a lista ser útil em vez de informativa.
export function separarPorAcao(linhas) {
  const l = Array.isArray(linhas) ? linhas : [];
  return { meus: l.filter(c => c.para_mim), ciencia: l.filter(c => !c.para_mim) };
}

// Mais antigo primeiro entre os abertos: chamado velho é o que dói.
// Entre os resolvidos, o mais recente primeiro — ali a pergunta é "o que
// aconteceu ultimamente", não "o que está apodrecendo".
export function ordenar(linhas, situacao) {
  const l = (Array.isArray(linhas) ? linhas : []).slice();
  const t = v => { const n = Date.parse(v || ''); return Number.isNaN(n) ? 0 : n; };
  if (situacao === 'resolvido') return l.sort((a, b) => t(b.fechado_em) - t(a.fechado_em) || t(b.aberto_em) - t(a.aberto_em));
  return l.sort((a, b) => t(a.aberto_em) - t(b.aberto_em));
}

// Quantos dias um chamado está aberto. Serve para a tarja de atraso: a
// camareira aperta "Solicito" e some; quem cobra é a lista.
export function diasAberto(aberto_em, agoraMs) {
  const t = Date.parse(aberto_em || '');
  if (Number.isNaN(t)) return null;
  const dias = Math.floor((agoraMs - t) / 86400000);
  return dias < 0 ? 0 : dias;
}

// O texto do atraso, ou nada. Três faixas e não um número solto: "há 9
// dias" exige que quem lê faça a conta do que é muito.
export function atrasoDe(aberto_em, agoraMs) {
  const d = diasAberto(aberto_em, agoraMs);
  if (d === null || d < 3) return null;
  if (d < 7)  return { texto: `há ${d} dias`, nivel: 'atencao' };
  if (d < 30) return { texto: `há ${d} dias`, nivel: 'tarde' };
  const meses = Math.floor(d / 30);
  return { texto: meses === 1 ? 'há mais de um mês' : `há mais de ${meses} meses`, nivel: 'tarde' };
}

// O título de um cartão: o que a pessoa lê primeiro, em duas partes.
// O item da ficha ("Televisão") quando veio de lá; "Intercorrência" quando
// não — porque ali a descrição é que diz o que houve, e repetir a primeira
// linha dela no título é ruído.
export function tituloDoChamado(c) {
  if (!c) return '';
  if (c.origem === 'ficha' && c.item_chave) {
    const rotulo = String(c.descricao || '').split('—')[0].trim();
    return rotulo || c.item_chave;
  }
  return origemDe(c.origem).rotulo;
}

// Os contadores do topo, por aba. Contam o que a pessoa VÊ — o corte de
// quem vê o quê já aconteceu no banco.
export function contarChamados(linhas) {
  const { meus, ciencia } = separarPorAcao(linhas);
  return { total: (linhas || []).length, meus: meus.length, ciencia: ciencia.length };
}

// Data curta para o cabeçalho do cartão: "17/09 · 14:32". O ano só aparece
// quando NÃO é o ano corrente — um chamado de 2025 ainda aberto tem de gritar
// isso, e escrever "/2026" em todos os outros só rouba espaço.
export function dataCurta(iso, agoraMs) {
  const d = new Date(iso || '');
  if (Number.isNaN(d.getTime())) return '';
  const p2 = n => String(n).padStart(2, '0');
  const dia = `${p2(d.getDate())}/${p2(d.getMonth() + 1)}`;
  const anoAgora = new Date(agoraMs).getFullYear();
  const ano = d.getFullYear() !== anoAgora ? `/${d.getFullYear()}` : '';
  return `${dia}${ano} · ${p2(d.getHours())}:${p2(d.getMinutes())}`;
}

// A frase da lista vazia. Não é enfeite: "nenhum chamado" e "nenhum chamado
// SEU" são notícias diferentes, e num hotel de 418 quartos a segunda é a
// que faz alguém fechar a tela tranquilo.
export function vazioDe(situacao, temAlgumNaOutraPilha) {
  if (situacao === 'resolvido') return 'Nenhum chamado resolvido ainda.';
  if (situacao === 'cancelado') return 'Nenhum chamado descartado.';
  if (temAlgumNaOutraPilha) return 'Nada para você resolver agora — os abertos são de outro responsável.';
  return 'Nenhum chamado aberto. Tudo em ordem.';
}

// ── as intercorrências da imobiliária (0099) ─────────────────────────
// O que o card do Dashboard dela conta e o modal lista: pendente é o
// escalado (o mais antigo primeiro: é o que está esperando há mais tempo);
// resolvida é o que passou pelo escalonamento e fechou (o mais recente
// primeiro, como o Histórico do MdM). O banco já só devolve a ela os
// escalados dos flats dela; o filtro de `escalado_em` aqui é a segunda
// trava, para a tela não contar como "dela" um chamado que nunca subiu.
export function separarIntercorrencias(linhas) {
  const l = Array.isArray(linhas) ? linhas.filter(c => c && c.escalado_em) : [];
  return {
    pendentes: l.filter(c => c.situacao === 'escalado').sort((a, b) => String(a.escalado_em).localeCompare(String(b.escalado_em))),
    resolvidas: l.filter(c => c.situacao === 'resolvido').sort((a, b) => String(b.fechado_em).localeCompare(String(a.fechado_em))),
  };
}
